import "./ReceiptsModal.scss";
import { useState, useEffect } from "react";
import useAuthStore from "../../../../store/authStore";
import { getInvoices } from "../../../../api/stripe/stripe";
import Spinner from "../../../Spinner/Spinner";
import { useTranslation } from "react-i18next";

function ReceiptsModal({ onClose }) {
  const { t, i18n } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const data = await getInvoices(token);
        setInvoices(data.invoices || []);
      } catch (err) {
        console.error("Failed to load invoices:", err);
        setError("receipts.loadFailed");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInvoices();
    }
  }, [token]);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatCurrency = (amount, currency) => {
    // eslint-disable-next-line no-undef
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  return (
    <div className="receipts-modal-content">
      <div className="title">
        <span>{t("userMenu.receipts")}</span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="main-wrapper">
        {loading ? (
          <div className="receipts-loading">
            <Spinner />
          </div>
        ) : error ? (
          <div className="receipts-error">{t(error)}</div>
        ) : invoices.length === 0 ? (
          <div className="receipts-empty">
            <span className="material-symbols-outlined">receipt_long</span>
            <p>{t("receipts.empty")}</p>
          </div>
        ) : (
          <div className="receipts-list">
            {invoices.map((invoice) => (
              <a
                key={invoice.id}
                href={invoice.invoicePdf || invoice.hostedInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`receipt-item ${invoice.type === "enterprise" ? "enterprise" : ""}`}
              >
                <div className="receipt-icon">
                  <span className="material-symbols-outlined">
                    {invoice.type === "enterprise" ? "business" : "description"}
                  </span>
                </div>
                <div className="receipt-info">
                  <div className="receipt-name">
                    {invoice.description ||
                      invoice.number ||
                      t("receipts.invoice")}
                    {invoice.type === "enterprise" && (
                      <span className="receipt-badge enterprise">
                        {t("receipts.enterprise")}
                      </span>
                    )}
                    {invoice.status === "paid" && (
                      <span className="receipt-badge paid">
                        {t("receipts.status.paid")}
                      </span>
                    )}
                    {invoice.status === "open" && (
                      <span className="receipt-badge open">
                        {t("receipts.status.open")}
                      </span>
                    )}
                  </div>
                  <div className="receipt-date">
                    {formatDate(invoice.created)}
                  </div>
                </div>
                <div className="receipt-amount">
                  {formatCurrency(invoice.amount, invoice.currency)}
                </div>
                <div className="receipt-action">
                  <span className="material-symbols-outlined">open_in_new</span>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReceiptsModal;
